export type LeadValidationReason =
  | "telefono_invalido"
  | "email_formato_incorrecto"
  | "correo_no_entregado";

export type LeadStatus = "validado" | "No es un lead" | "Correo no entregado";

export interface LeadContactInput {
  id?: string;
  email?: string | null;
  phone?: string | null;
}

export interface LeadValidationIssue {
  field: "phone" | "email" | "emailDelivery";
  reason: LeadValidationReason;
  message: string;
}

export interface LeadValidationResult {
  valid: boolean;
  status: LeadStatus;
  issues: LeadValidationIssue[];
}

export interface LeadValidationReport<T extends LeadContactInput = LeadContactInput> {
  totalProcessed: number;
  validatedCount: number;
  rejectedCount: number;
  successRate: number;
  validLeads: T[];
  rejectedLeads: Array<{
    lead: T;
    status: LeadStatus;
    reasons: LeadValidationIssue[];
  }>;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PHONE_VALID_REGEX = /^\+?[0-9]+$/;

export function isValidEmailFormat(email: string): boolean {
  return EMAIL_REGEX.test(email.trim());
}

export function isDigitsOnlyPhone(phone: string): boolean {
  return PHONE_VALID_REGEX.test(phone);
}

export function validateLeadContact(input: LeadContactInput): LeadValidationResult {
  const issues: LeadValidationIssue[] = [];
  const email = input.email?.trim() ?? "";
  const phone = input.phone ?? "";

  if (phone && !isDigitsOnlyPhone(phone)) {
    issues.push({
      field: "phone",
      reason: "telefono_invalido",
      message: "El teléfono solo puede contener números y opcionalmente un '+' al inicio.",
    });
  }

  if (email && !isValidEmailFormat(email)) {
    issues.push({
      field: "email",
      reason: "email_formato_incorrecto",
      message: "El correo electrónico no tiene un formato válido.",
    });
  }

  return {
    valid: issues.length === 0,
    status: issues.some((issue) => issue.field === "email") ? "No es un lead" : "validado",
    issues,
  };
}

export function buildLeadValidationReport<T extends LeadContactInput>(
  leads: T[],
  results: LeadValidationResult[],
): LeadValidationReport<T> {
  const validLeads: T[] = [];
  const rejectedLeads: LeadValidationReport<T>["rejectedLeads"] = [];

  leads.forEach((lead, index) => {
    const result = results[index];
    if (result?.valid) {
      validLeads.push(lead);
    } else if (result) {
      rejectedLeads.push({ lead, status: result.status, reasons: result.issues });
    }
  });

  const totalProcessed = leads.length;
  const validatedCount = validLeads.length;
  const rejectedCount = rejectedLeads.length;

  return {
    totalProcessed,
    validatedCount,
    rejectedCount,
    successRate: totalProcessed === 0 ? 0 : Number(((validatedCount / totalProcessed) * 100).toFixed(2)),
    validLeads,
    rejectedLeads,
  };
}

export function buildSingleLeadValidationReport<T extends LeadContactInput>(
  lead: T,
  result: LeadValidationResult,
): LeadValidationReport<T> {
  return buildLeadValidationReport([lead], [result]);
}
